/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import * as actionTypes from '../constants/action-types';
import { API } from '../constants/errors';
import { handleLogin } from './auth';
import { showLogin } from './login';
import { getAnalytics } from '../services/analytics/AnalyticsService';
import { AnalyticsEvents } from '../services/analytics/AnalyticsEvents';
import { CurrentUserData, UserThunk } from '../reducers/current-user';
import { OnboardingToken } from '../types/onboarding';
import G17Client, { formDataConfig } from '../services/G17Client';
import axios, { Canceler } from 'axios';
import { withProfile } from '../utils/files';

const analytics = getAnalytics();

let cancel: Canceler;

export function loadCurrentUser(refresh = false, forcedUserId?: string): UserThunk {
  return (dispatch, getState) => {
    const usersState = getState().currentUser;
    if (usersState.loading) {
      if (!refresh) {
        return;
      }
      if (cancel) {
        cancel(); // Cancel current request, will trigger a new one
      }
    }

    dispatch({ type: actionTypes.LOADING_CURRENT_USER });
    return G17Client.get<{ data: CurrentUserData }>('users/current', {
      cancelToken: G17Client.createCancelToken((c) => {
        cancel = c;
      }),
    })
      .then((response) => {
        if (forcedUserId) {
          if (forcedUserId !== response.data.data._id) {
            return showLogin();
          }
        }
        dispatch({
          type: actionTypes.LOADED_CURRENT_USER,
          data: response.data.data,
        });
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          return; // Do nothing, only cancel when new request starting
        }
        const msg = error.safe ? error.message : API.GENERIC;
        if (error.response && error.response.status === 403) {
          dispatch({ type: actionTypes.SHOW_PERMISSION_DENIED });
        }
        dispatch({ type: actionTypes.ERROR_CURRENT_USER, errorMessage: msg })
      });
  }
}

export function reloadCurrentUser() {
  return loadCurrentUser(true);
}

export const onboardUser = async (token: OnboardingToken, form: {}) => {
  return G17Client.post(`/direct/onboarding/user/token/${token.token}`, withProfile(form), formDataConfig)
    .then((response) => {
      analytics.track(AnalyticsEvents.UserRegistered, { onboardId: token._id, initiativeId: token.initiativeId });
      return response.data
    })
};

export const rejectOnboardUser = async (token: OnboardingToken) => {
  return G17Client.post(`/direct/onboarding/user/token/${token.token}/reject`)
    .then(async (response) => {
      const analytics = getAnalytics();
      await analytics.track(AnalyticsEvents.UserInvitationReject, {
        onboardId: token._id,
        initiativeId: token.initiativeId,
      });
      return { success: response.data.success }
    })
    .catch((error) => ({ success: false, message: error.message }))
};

export const onboardUserOnSurvey = async (token: string, action: 'accept' | 'reject') => {
  return G17Client.post(`/onboarding/token/${token}/${action}`)
    .then((response) => {
      const { success, message } = response.data;

      if (success !== true) {
        throw new Error(message);
      }
      return response.data

    }).catch((error) => {
      return {
        success: false,
        message: error.message,
      }
    })
};

export const saveUser = async (data: {}, files: File[]) => {
  const form = { ...data, files };
  return G17Client.patch('/users/current', withProfile(form), formDataConfig)
    .then((response) => {
      const { success } = response.data;
      return {
        success
      }
    })
};

export const registerUser = async (formData: Record<string, unknown>) => {
  return G17Client.post('/direct/onboarding/user/register', formData)
    .then(async (response) => {
      const { success } = response.data;

      await analytics.track(AnalyticsEvents.UserRegistered);
      return {
        success
      }
    })
};

export const activateUser = async (formData: Record<string, string>, userId: string, token: string, redirectUrl?: string) => {
  return G17Client.post(`/direct/onboarding/user/${userId}/${token}/activate`, formData)
    .then(async (response) => {
      const { success } = response.data;

      await analytics.track(AnalyticsEvents.UserActivated);
      await handleLogin(redirectUrl);
      return {
        success
      }
    })
};

// User's companies
export const searchReportingInitiativesExtended = () => G17Client.searchReportingInitiative(true);
